import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { useFeedbackScreenshotImage, useFeedbackScreenshots } from '../api/hooks';
import type { FeedbackScreenshot } from '../api/types';

/** An object URL for the blob, revoked when the blob changes or the view goes. */
function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blob]);
  return url;
}

function sentAtLabel(screenshot: FeedbackScreenshot): string {
  return screenshot.sentAt ? `sent ${new Date(screenshot.sentAt).toLocaleString()}` : '';
}

function ScreenshotTile({
  feedbackId,
  screenshot,
}: {
  feedbackId: string;
  screenshot: FeedbackScreenshot;
}) {
  const image = useFeedbackScreenshotImage(feedbackId, screenshot.id);
  const url = useObjectUrl(image.data);
  const [open, setOpen] = useState(false);
  const label = `${screenshot.filename} ${sentAtLabel(screenshot)}`.trim();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!url}
        aria-label={`Open screenshot ${screenshot.filename}`}
        title={label}
        className="flex h-28 w-40 items-center justify-center overflow-hidden rounded-3xl border bg-background text-xs text-muted-foreground hover:border-foreground/40"
      >
        {url ? (
          <img src={url} alt={screenshot.filename} className="h-full w-full object-cover" />
        ) : image.isError ? (
          'Could not load'
        ) : (
          'Loading…'
        )}
      </button>
      {url && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>{screenshot.filename}</DialogTitle>
              <DialogDescription>
                Attached by the reporter in the chat
                {screenshot.sentAt ? `, ${sentAtLabel(screenshot)}` : ''}.
              </DialogDescription>
            </DialogHeader>
            <img
              src={url}
              alt={screenshot.filename}
              className="max-h-[75vh] w-full rounded-3xl object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/** The images the reporter attached in the ticket's chat; nothing when there are none. */
export function FeedbackScreenshots({ feedbackId }: { feedbackId: string }) {
  const screenshots = useFeedbackScreenshots(feedbackId);
  if (screenshots.isError) {
    return (
      <div className="mt-4 text-xs text-muted-foreground">Screenshots could not be loaded.</div>
    );
  }
  const items = screenshots.data ?? [];
  if (items.length === 0) return null;
  return (
    <div className="mt-4 space-y-1">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Screenshots
      </div>
      <div className="text-xs text-muted-foreground">Start fix sends these to Code.</div>
      <div className="flex flex-wrap gap-2">
        {items.map(screenshot => (
          <ScreenshotTile key={screenshot.id} feedbackId={feedbackId} screenshot={screenshot} />
        ))}
      </div>
    </div>
  );
}
